import { memo } from 'react';
import type { VNode } from 'preact';
import { Chip, type IconPath } from '@nespresso-design-system/components';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService';
import { trackFiltersApplied, trackFiltersRemoval } from '../../../Tracking.ts';
import styles from '../AllFiltersDrawer.module.scss';
import type {
  Filter,
  FilterOption as FilterOptionType
} from '@/domain/FilteringAndSorting.ts';

type FilterListProps = {
  filtering: FiltersAndSort['filtering'];
};

type FilterOptionProps = FilterListProps & {
  filter: Filter;
  option: FilterOptionType;
};

const QUICK_FILTERS = 'QUICK';

function FilterList({ filtering }: FilterListProps) {
  return filtering.filters.value
    .filter((filter) => filter.type !== QUICK_FILTERS)
    .map((filter) => (
      <div class={styles.filterSection} key={filter.key}>
        <h3 class={styles.filterTitle}>{filter.label}</h3>
        <ul class={styles.container}>
          {filter.options.map((option) => (
            <li key={option.id}>
              <FilterOption
                filter={filter}
                filtering={filtering}
                option={option}
              />
            </li>
          ))}
        </ul>
      </div>
    )) as unknown as VNode;
}

export default memo(FilterList);

const FilterOption = memo(
  ({ filter, filtering, option }: FilterOptionProps) => {
    const { icon, label, id } = option;
    const { id: filterId } = filter;
    const isSelected = option.selected;

    return (
      <Chip
        aria-label={label}
        label={label}
        isSelected={isSelected}
        disabled={filtering.isDisabledFilterOption(filterId, id).value}
        onClick={() => {
          if (isSelected) {
            trackFiltersRemoval([[filter, option]], 'clear all chip');
            filtering.updateFilter(filterId, id, !isSelected);
          } else {
            filtering.updateFilter(filterId, id, !isSelected);
            trackFiltersApplied(filtering.currentFilters.value);
          }
        }}
        icon={icon as IconPath}
      />
    );
  }
);
